/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_ADMIN_WORKING_DATA_BOX_TYPING
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Allow previewing working data for users without email addresses.
 *
 * File Size Check:
 * - Current: 334 lines
 * - Adding: ~12 lines
 * - Projected: ~346 lines
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/features/admin/users/AdminWorkingDataBox.tsx
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_USERS_PAGE_REFINED, USER_WORKING_DATA_PREVIEW_UI
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Refined UsersPage component to resolve compilation issues.
 * Used standard function declaration and direct export.
 * Added preview modal for user working data cleanup.
 */

// [2026-02-07] style-extraction: moved inline styles to SCSS module
import * as React from "react";
import { Modal, Table, Badge, Spinner, Button, Alert } from "react-bootstrap";
import UsersFilters from "../../features/admin/users/UsersFilters";
import styles from "./Admin.module.scss";
import UsersTable from "../../features/admin/users/UsersTable";
import BulkBar from "../../features/admin/users/BulkBar";
import UserEditModal from "../../features/admin/users/UserEditModal";
import AdminSignupReportPanel from "../../features/admin/users/AdminSignupReportPanel";
import AdminWorkingDataBox from "../../features/admin/users/AdminWorkingDataBox";
import { useUsersQuery } from "../../features/admin/users/hooks/useUsersQuery";
import { deleteUserWorkingTables, previewUserWorkingTables } from "../../services/adminUsersService";

export default function UsersPage() {
  const { params, setParams, data, isLoading, isError, refetch } = useUsersQuery();
  const [selected, setSelected] = React.useState<string[]>([]);
  const [editId, setEditId] = React.useState<string | null>(null);
  const [selectedUserForCleanup, setSelectedUserForCleanup] = React.useState<string | null>(null);

  // [2025-12-30] Data cleanup: Add state for user working data preview
  const [showUserWorkingPreviewModal, setShowUserWorkingPreviewModal] = React.useState(false);
  const [userWorkingPreviewData, setUserWorkingPreviewData] = React.useState<any>(null);
  const [userWorkingPreviewLoading, setUserWorkingPreviewLoading] = React.useState(false);
  const [userWorkingCleanupLoading, setUserWorkingCleanupLoading] = React.useState(false);
  const [selectedUserForPreview, setSelectedUserForPreview] = React.useState<{id: string, name: string, email?: string} | null>(null);
  // [2026-06-26] working-data-gate: Clean is locked until Preview has loaded data for this user
  const [previewedUserId, setPreviewedUserId] = React.useState<string | null>(null);
  // [2026-01-07] Working data preview: tolerate missing email addresses

  const handleRowSelect = (ids: string[]) => setSelected(ids);
  const handleEdit = (id: string) => setEditId(id);
  const closeEdit = () => setEditId(null);

  // [2025-12-30] Data cleanup: Format cleanup results for display
  const formatCleanupResults = (results: any): string => {
    const lines: string[] = [];
    
    Object.entries(results).forEach(([key, value]) => {
      if (key === 'totalOrphaned' || key === 'totalDeleted') {
        return; // Skip, will show at bottom
      }
      if (key === 'collectionsProcessed' || key === 'collectionsWithOrphans') {
        return; // Skip metadata
      }
      if (key === 'userName' || key === 'userEmail') {
        return; // Skip user info
      }
      if (typeof value === 'number' && value > 0) {
        lines.push(`• ${key}: ${value} records`);
      }
    });
    
    return lines.join('\n');
  };

  const handleExport = () => {
    // Export is handled in UsersFilters component
    console.log("Export completed");
  };

  const handleSaved = () => {
    closeEdit();
    refetch();
  };

  // [2025-01-13] user-delete: handle user deletion callback
  const handleUserDeleted = (userId: string) => {
    // [2025-01-13] user-delete: remove deleted user from selected list if present
    setSelected((prev) => prev.filter((id) => id !== userId));
    // [2025-01-13] user-delete: refresh user list to reflect deletion
    refetch();
  };

  // Resolve a single selected user object (if exactly one selected)
  const selectedUser = React.useMemo(() => {
    if (selected.length !== 1) return null;
    const user = (data?.items || []).find((u: any) => u._id === selected[0]);
    if (!user) return null;
    const name = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "Unknown";
    return { id: user._id, name, email: user.email };
  }, [selected, data]);

  // [2026-06-26] working-data-gate: re-lock Clean whenever the selected user changes
  // (keep the unlock only while it still matches the currently selected user)
  React.useEffect(() => {
    setPreviewedUserId((prev) => (prev && selectedUser && prev === selectedUser.id ? prev : null));
  }, [selectedUser]);

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm("Are you sure you want to delete this user? This will perform a soft delete.")) {
      return;
    }
    try {
      const { deleteUser } = await import("../../services/adminUsersService");
      await deleteUser(userId);
      handleUserDeleted(userId);
      alert("User deleted successfully");
    } catch (error: any) {
      console.error("Delete user failed:", error);
      alert(`Delete failed: ${error.message}`);
    }
  };

  // [2026-06-23] impersonation: start "View as User" (read-only), then full-reload as the user
  const handleImpersonate = async (userId: string) => {
    try {
      const { startImpersonation } = await import("../../services/adminUsersService");
      await startImpersonation(userId);
      // Full reload so the new (user) cookie is picked up and lands on the user's dashboard
      window.location.href = "/";
    } catch (error: any) {
      const msg =
        error?.response?.data?.error || error.message || "Failed to start View as User";
      alert(msg);
    }
  };

  // [2025-12-30] Data cleanup: Handler for user working data preview
  const handlePreviewUserWorkingData = async (userId: string, userName: string, userEmail?: string) => {
    // [2026-01-07] Working data preview: accept optional email input
    setSelectedUserForPreview({ id: userId, name: userName, email: userEmail });
    setUserWorkingPreviewLoading(true);
    setShowUserWorkingPreviewModal(true);
    
    try {
      const preview = await previewUserWorkingTables(userId);
      setUserWorkingPreviewData(preview);
      // [2026-06-26] working-data-gate: preview succeeded — unlock Clean for this user
      setPreviewedUserId(userId);
    } catch (error: any) {
      alert(`❌ Failed to preview working data:\n${error.message}`);
      setShowUserWorkingPreviewModal(false);
      setSelectedUserForPreview(null);
    } finally {
      setUserWorkingPreviewLoading(false);
    }
  };

  // [2025-12-30] Data cleanup: Handler for confirming user working data cleanup
  const handleConfirmUserWorkingCleanup = async () => {
    if (!selectedUserForPreview) return;
    
    setUserWorkingCleanupLoading(true);
    setSelectedUserForCleanup(selectedUserForPreview.id);
    
    try {
      const result = await deleteUserWorkingTables(selectedUserForPreview.id);
      
      const resultDetails = formatCleanupResults(result);
      
      const successMessage = 
        `✅ Working Data Cleaned for ${selectedUserForPreview.name}!\n\n` +
        '═══════════════════════════════════════════\n\n' +
        'Collections Cleaned:\n\n' +
        (resultDetails || "No working data found.") +
        '\n\n═══════════════════════════════════════════\n\n' +
        `📊 Total Records Deleted: ${result.totalDeleted}`;
      
      alert(successMessage);
      setShowUserWorkingPreviewModal(false);
      setUserWorkingPreviewData(null);
      setSelectedUserForPreview(null);
    } catch (error: any) {
      alert(`❌ Cleanup failed:\n${error.message}`);
    } finally {
      setUserWorkingCleanupLoading(false);
      setSelectedUserForCleanup(null);
    }
  };

  return (
    <div className={`d-flex flex-column gap-3 ${styles.fullWidthContainer}`}>
      <UsersFilters params={params} onChange={setParams} onExport={handleExport} onRefresh={refetch} />

      {selected.length > 0 && (
        <BulkBar selected={selected} clearSelection={() => setSelected([])} onDone={refetch} />
      )}

      <UsersTable
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={data?.page ?? 1}
        limit={data?.limit ?? 10}
        sortBy={params.sortBy ?? "createdAt"}
        sortOrder={params.sortOrder ?? "desc"}
        loading={isLoading}
        error={isError}
        onSort={(sortBy, sortOrder) => setParams({ sortBy, sortOrder })}
        onPageChange={(page) => setParams({ page })}
        onLimitChange={(limit) => setParams({ limit, page: 1 })}
        selected={selected}
        onSelectChange={handleRowSelect}
        onEdit={handleEdit}
        onDelete={handleDeleteUser}
        onImpersonate={handleImpersonate}
        onPreviewWorkingData={handlePreviewUserWorkingData}
        selectedUserForCleanup={selectedUserForCleanup}
      />

      {/* [2025-12-30] Data cleanup: User Working Data Preview Modal */}
      <Modal 
        show={showUserWorkingPreviewModal} 
        onHide={() => !userWorkingCleanupLoading && setShowUserWorkingPreviewModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton={!userWorkingCleanupLoading}>
          <Modal.Title>
            Working Data Preview
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {userWorkingPreviewLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted">Scanning user data across collections...</p>
            </div>
          ) : userWorkingPreviewData ? (
            <>
              {/* User Info */}
              <div className="mb-4 p-3 bg-light rounded border">
                <div className="row">
                  <div className="col-sm-6"><strong>User:</strong> {userWorkingPreviewData.userName}</div>
                  <div className="col-sm-6"><strong>Email:</strong> {userWorkingPreviewData.userEmail}</div>
                </div>
              </div>

              {userWorkingPreviewData.totalToDelete === 0 ? (
                <Alert variant="info" className="text-center">
                  <div className="h5 mb-2">No working data found for this user!</div>
                  This user has no data in operational or working tables.
                </Alert>
              ) : (
                <>
                  <Alert variant="warning" className="mb-4">
                    <strong>Warning:</strong> This action will permanently delete user operational data. This cannot be undone!
                  </Alert>
                  
                  <div className="mb-4">
                    <h6 className="border-bottom pb-2 mb-3">Summary:</h6>
                    <ul className="list-unstyled">
                      <li>• Total records to delete: <strong>{userWorkingPreviewData.totalToDelete}</strong></li>
                      <li>• Collections affected: <strong>{
                        Object.keys(userWorkingPreviewData)
                          .filter(k => !['totalToDelete', 'userName', 'userEmail', 'userId', 'excludedCollections'].includes(k))
                          .length
                      }</strong></li>
                    </ul>
                  </div>

                  <div className="mb-4">
                    <h6 className="border-bottom pb-2 mb-3 text-success">✅ Protected Collections (will NOT be deleted):</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {userWorkingPreviewData.excludedCollections.map((collection: string) => (
                        <Badge key={collection} bg="success" pill>
                          {collection}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <h6 className="border-bottom pb-2 mb-3 text-danger">🗑️ Working Data to be Deleted:</h6>
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead className="table-light">
                        <tr>
                          <th>Collection</th>
                          <th className="text-end">Records to Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(userWorkingPreviewData)
                          .filter(([key]) => !['totalToDelete', 'userName', 'userEmail', 'userId', 'excludedCollections'].includes(key))
                          .filter(([, value]) => typeof value === 'number' && (value as number) > 0)
                          .map(([collection, count]) => (
                            <tr key={collection}>
                              <td>{collection}</td>
                              <td className="text-end">{count as number}</td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </>
          ) : (
            <Alert variant="danger" className="text-center">
              Failed to load preview data. Please try again.
            </Alert>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowUserWorkingPreviewModal(false);
              setUserWorkingPreviewData(null);
              setSelectedUserForPreview(null);
            }}
            disabled={userWorkingCleanupLoading}
          >
            Cancel
          </Button>
          
          {userWorkingPreviewData && userWorkingPreviewData.totalToDelete > 0 && (
            <Button
              variant="danger"
              onClick={handleConfirmUserWorkingCleanup}
              disabled={userWorkingCleanupLoading}
            >
              {userWorkingCleanupLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                <>
                  Delete {userWorkingPreviewData.totalToDelete} Records
                </>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <UserEditModal
        id={editId}
        onClose={closeEdit}
        onSaved={handleSaved}
        onUserDeleted={handleUserDeleted}
      />
      <AdminWorkingDataBox
        selectedUser={selectedUser}
        previewedUserId={previewedUserId}
        onOpenPreview={handlePreviewUserWorkingData}
        onCleanCompleted={() => {
          // [2026-06-26] working-data-gate: re-lock Clean after a successful cleanup
          setPreviewedUserId(null);
          // Refresh list after cleaning
          refetch();
        }}
      />

      {/* [2026-06-26] Removed AdminInventoryUploadPanel — admin per-user upload box no longer required */}
      <AdminSignupReportPanel />
    </div>
  );
}
